import { useMemo, useState, type ReactNode } from "react";

import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Assets,
  useGetV1Plants,
  useGetV1SitesIdGraph,
  type EnvelopeAssetGraphDataEdgesItem,
  type EnvelopeAssetGraphDataNodesItem,
  type EnvelopeAssetListDataItemsItem,
  type FigureState,
  type Reason,
} from "@solarkey/api-client";
import {
  Banner,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
  StateWord,
  equipmentClass,
  equipmentClassLabel,
} from "@solarkey/ui";

/* ════════════════════════════════════════════════════════════════════════════
   M6 — Assets and topology (onboarding wizard, wireframe §08, Admin). Step 3:
   Connectivity — the graph shown as a table (parent, child, edge, rating,
   source), with the draft schematic and the checks-before-publish beside it.

   Every fact on this screen is the site's own, served by
   `GET /v1/sites/{id}/graph` (nodes, edges, meter groups, topology version) and
   `GET /v1/assets?site=` (the device registry: make, model, ratings, meter
   group, whether the device is instrumented). Nothing is invented: a field the
   contract does not carry is withheld as a state word with its reason, never a
   zero and never a dash (UX-003 §3.1), and a device with no parent edge is a
   real count off the served graph or it is withheld.

   Build 01 serves no onboarding WRITE path, so every write control here (import
   as-built, add/bulk-edit an edge, publish the topology) keeps its place and
   says it is not wired instead of looking live and doing nothing.
   Component classes → fonts match.
   ════════════════════════════════════════════════════════════════════════════ */

// The wizard's step list is UI structure, not data — it stands whatever the
// estate holds.
const STEPS = [
  "Site and configuration",
  "Devices and data sources",
  "Connectivity",
  "Meter groups and blocks",
  "Review and publish",
];

/** Templated reasons (UX-003 §3.8) — no free text ever enters a reason. */
const R = {
  /** No edge at all is loaded for the site. */
  noTopology: { template: "NOT_MEASURED_NO_TOPOLOGY_EDGES" },
  /** Edges exist, but this device sits on none of them. */
  noParentEdge: { template: "NOT_MEASURED_SLD_POSITION_NOT_RECORDED" },
  /** The graph/registry carries no such field (e.g. an edge's source). */
  fieldNotAuthored: { template: "NOT_MEASURED_REGISTRY_FIELD_NOT_AUTHORED" },
  noRating: { template: "NOT_MEASURED_NO_NAMEPLATE_RATING" },
  noMeterMap: { template: "NOT_MEASURED_METER_GROUPS_NOT_AUTHORED" },
  noSignalInventory: { template: "CANNOT_CHECK_SIGNAL_INVENTORY_UNAVAILABLE" },
  /** The registry read itself failed, so an empty answer means nothing. */
  registryUnreadable: { template: "CANNOT_CHECK_REGISTRY_UNREADABLE" },
} satisfies Record<string, Reason>;

/* A rating's unit comes from the field that carries it — never from a literal
   guess (CLAUDE.md rule 3 / UX-003 §6.2). First field present wins. */
const RATING_FIELDS = [
  ["rated_kw", "kW"],
  ["rated_kva", "kVA"],
  ["rated_kwh", "kWh"],
  ["rated_dc_kwp", "kWp"],
] as const;

type GraphNode = EnvelopeAssetGraphDataNodesItem & { id: string };
type GraphEdge = EnvelopeAssetGraphDataEdgesItem & { from_id: string; to_id: string };

interface Row {
  key: string;
  /** Parent device label, or null when the graph places this device nowhere. */
  parent: string | null;
  edgeType: string | null;
  child: string;
  rating: string | null;
  unassigned: boolean;
}

type Mark = "ok" | "warning" | "fault" | "withheld";
interface Check {
  mark: Mark;
  label: string;
  detail: ReactNode;
}

export function Onboarding({ site: siteProp }: { site?: string } = {}): ReactNode {
  const [step] = useState(2); // step 3 — Connectivity
  // The screen never assumes a site: it is chosen from the served fleet list.
  const [site, setSite] = useState<string | null>(siteProp ?? null);

  const plantsQuery = useGetV1Plants();
  // The hook is disabled while the id is empty, so no site means no request.
  const graphQuery = useGetV1SitesIdGraph(site ?? "");
  const assetsQuery = useGetV1Assets(site ? { site } : undefined, {
    query: { enabled: !!site },
  });

  /* Site identifiers only — never a plant or customer name (CLAUDE.md rule 5). */
  const siteOptions = useMemo(() => {
    if (plantsQuery.data?.status !== 200) return [];
    const seen = new Set<string>();
    for (const item of plantsQuery.data.data.data.items ?? []) {
      if (typeof item.site_id === "string" && item.site_id) seen.add(item.site_id);
    }
    return [...seen].sort().map((id) => ({ key: id, label: id }));
  }, [plantsQuery.data]);

  let scopeLine = "Connectivity is data; the schematic, meter groups and reach resolve from it.";
  let versionChip: ReactNode = null;
  let body: ReactNode;

  if (!site) {
    scopeLine = "No site selected — the wizard reads the chosen site's own asset graph.";
    body = (
      <EmptyState
        variant="no_data"
        title="No site selected"
        message="Choose a site identifier above. The wizard shows only that site's served topology; it never assumes one."
      />
    );
  } else if (graphQuery.isLoading) {
    scopeLine = `Site ${site} · reading the asset graph`;
    body = (
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14, alignItems: "start" }} aria-busy="true">
        <div className="card">
          <Skeleton variant="text" width="10rem" />
          <Skeleton variant="row" lines={6} />
        </div>
        <div className="card">
          <Skeleton variant="tile" />
        </div>
      </div>
    );
  } else if (graphQuery.isError || !graphQuery.data || graphQuery.data.status !== 200) {
    scopeLine = `Site ${site} · the asset graph could not be read`;
    body = (
      <ErrorState
        title="Topology is unavailable"
        message="The plant topology could not be loaded, so no connectivity is shown."
        onRetry={() => void graphQuery.refetch()}
      />
    );
  } else {
    const g = graphQuery.data.data.data;

    /* ── The served graph, and nothing else ──────────────────────────────── */
    const nodes = (g.nodes ?? []).filter((n): n is GraphNode => typeof n.id === "string" && !!n.id);
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    // Both endpoints must resolve to a node we were served; a dangling edge is
    // dropped, never faked (same rule as the schematic).
    const edges = (g.edges ?? []).filter(
      (e): e is GraphEdge =>
        !!e.from_id && !!e.to_id && nodeById.has(e.from_id) && nodeById.has(e.to_id),
    );
    const meterGroups = g.meter_groups ?? [];
    const noEdges = edges.length === 0;

    /* ── The registry, for per-device ratings and the source mapping ─────── */
    const assetsOk = assetsQuery.data?.status === 200;
    const assetItems: EnvelopeAssetListDataItemsItem[] = assetsOk
      ? (assetsQuery.data?.data.data.items ?? [])
      : [];
    const assetById = new Map<string, EnvelopeAssetListDataItemsItem>();
    for (const a of assetItems) if (typeof a.id === "string" && a.id) assetById.set(a.id, a);
    // The registry read states which columns it cannot fill, and why.
    const assetWithheld = assetsOk
      ? (((assetsQuery.data?.data.data as unknown as { withheld?: Record<string, Reason | undefined> })
          .withheld ?? {}) as Record<string, Reason | undefined>)
      : {};

    const label = (id: string) => nodeById.get(id)?.role_name ?? assetById.get(id)?.role_name ?? id;

    /** A device's plated rating, from the graph node or the registry row. */
    const nameplate = (id: string): string | null => {
      const fromGraph = nodeById.get(id)?.ratings ?? {};
      const fromRegistry = assetById.get(id)?.ratings ?? {};
      for (const [field, unit] of RATING_FIELDS) {
        const v = fromGraph[field] ?? fromRegistry[field];
        if (typeof v === "number") return `${v} ${unit}`;
      }
      return null;
    };

    /* ── Rows: one per served edge, then every device the graph places nowhere.
          A device is never dropped — the field the graph cannot fill is. ───── */
    const rows: Row[] = edges.map((e, i) => ({
      key: `edge-${e.from_id}-${e.to_id}-${i}`,
      parent: label(e.from_id),
      edgeType: edgeType(e),
      child: label(e.to_id),
      rating: edgeRating(e) ?? nameplate(e.to_id),
      unassigned: false,
    }));
    const touched = new Set<string>();
    for (const e of edges) {
      touched.add(e.from_id);
      touched.add(e.to_id);
    }
    const unassigned = nodes.filter((n) => !touched.has(n.id));
    for (const n of unassigned) {
      rows.push({
        key: `unassigned-${n.id}`,
        parent: null,
        edgeType: null,
        child: n.role_name ?? n.id,
        rating: nameplate(n.id),
        unassigned: true,
      });
    }
    // With no edge at all the topology simply is not loaded; with edges present,
    // a device on none of them has no recorded position. Two different truths.
    const parentReason: Reason = noEdges ? R.noTopology : R.noParentEdge;

    /* ── Composition of the served graph, by device class ────────────────── */
    const classCounts = new Map<string, number>();
    for (const n of nodes) {
      const cls = equipmentClassLabel(n.device_class);
      classCounts.set(cls, (classCounts.get(cls) ?? 0) + 1);
    }

    /* ── Checks before publish — derived, or withheld with a reason ──────── */
    const missingRating = nodes.filter((n) => nameplate(n.id) === null).length;
    const meters = assetItems.filter((a) => equipmentClass(a.device_class) === "meter");
    const metersNoGroup = meters.filter((m) => !m.meter_group_id).length;
    const instrumented = assetItems
      .map((a) => (a as unknown as { monitored?: unknown }).monitored)
      .filter((m): m is boolean => typeof m === "boolean");
    const notInstrumented = instrumented.filter((m) => !m).length;

    const checks: Check[] = [
      noEdges
        ? {
            mark: "withheld",
            label: "Connectivity loaded",
            detail: <StateWord state={st("NOT_MEASURED")} reason={R.noTopology} />,
          }
        : {
            mark: "ok",
            label: "Connectivity loaded",
            detail: `${edges.length} edges across ${nodes.length} devices, as served`,
          },
      noEdges
        ? {
            mark: "withheld",
            label: "Devices with no parent edge",
            detail: <StateWord state={st("NOT_MEASURED")} reason={R.noTopology} />,
          }
        : {
            mark: unassigned.length === 0 ? "ok" : "warning",
            label: "Devices with no parent edge",
            detail: `${unassigned.length} of ${nodes.length} · allowed; they render as unassigned`,
          },
      nodes.length === 0
        ? {
            mark: "withheld",
            label: "Ratings present on every device",
            detail: <StateWord state={st("NOT_MEASURED")} reason={R.noRating} />,
          }
        : {
            mark: missingRating === 0 ? "ok" : "warning",
            label: "Ratings present on every device",
            detail: `${missingRating} of ${nodes.length} devices carry no plated rating`,
          },
      meterGroupCheck(assetsOk, assetWithheld, meters.length, metersNoGroup),
      instrumentCheck(assetsOk, instrumented.length, notInstrumented),
      {
        // No endpoint on this screen states the expected signals per device
        // class, so completeness cannot be claimed.
        mark: "withheld",
        label: "Expected signals per device class set",
        detail: <StateWord state={st("CANNOT_CHECK")} reason={R.noSignalInventory} />,
      },
    ];

    scopeLine = `Site ${site} · ${nodes.length} devices served · commissioning`;
    versionChip = (
      <span className="chip">
        Topology version ·{" "}
        {g.topology_version ?? <StateWord state={st("NOT_MEASURED")} reason={R.fieldNotAuthored} />}
      </span>
    );

    body = (
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14, alignItems: "start" }}>
        {/* ── Connectivity table (annotation 1) ── */}
        <div className="card">
          <h4>
            Connectivity
            <span className="sub">
              parent, child, edge type, rating · as served by the site&apos;s asset graph
            </span>
          </h4>
          {noEdges && nodes.length > 0 ? (
            <Banner tone="info">
              Topology pending — the connectivity between these devices is drawn from the
              estate registry export, which isn’t loaded for this site yet. The devices
              below are real; their parents are not recorded.
            </Banner>
          ) : null}
          {rows.length === 0 ? (
            <EmptyState variant="no_data" title="No topology reported" />
          ) : (
            <>
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <table className="table" aria-label="Connectivity">
                  <thead>
                    <tr>
                      <th>Parent</th>
                      <th>Edge</th>
                      <th>Child</th>
                      <th>Rating</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.key}
                        style={r.unassigned ? { background: "var(--sk-color-accent-soft)" } : undefined}
                      >
                        <td
                          style={{
                            fontWeight: r.unassigned ? 400 : 600,
                            color: r.unassigned ? "var(--sk-color-ink-3)" : undefined,
                          }}
                        >
                          {r.parent ?? <StateWord state={st("NOT_MEASURED")} reason={parentReason} />}
                        </td>
                        <td>
                          {r.edgeType ?? (
                            <StateWord state={st("NOT_MEASURED")} reason={parentReason} />
                          )}
                        </td>
                        <td>{r.child}</td>
                        <td>
                          {r.rating ?? <StateWord state={st("NOT_MEASURED")} reason={R.noRating} />}
                        </td>
                        <td>
                          {/* The graph carries no per-edge provenance, so the
                              source of a connection is withheld, not guessed. */}
                          <StateWord state={st("NOT_MEASURED")} reason={R.fieldNotAuthored} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--sk-color-ink-3)" }}>
                A highlighted row is a device the graph places nowhere. Ratings are the
                device&apos;s plated figures from the registry, in the unit the field carries.
              </p>
            </>
          )}
          {/* Editing connectivity is a write; Build 01 serves no topology write
              path, so the controls say so rather than looking live. */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <button
              type="button"
              className="btn small"
              style={notWiredStyle}
              disabled
              aria-disabled="true"
              title="Adding an edge is not wired yet: the platform serves no topology write endpoint."
            >
              Add edge · not wired
            </button>
            <button
              type="button"
              className="btn small"
              style={notWiredStyle}
              disabled
              aria-disabled="true"
              title="Bulk editing is not wired yet: the platform serves no topology write endpoint."
            >
              Bulk edit · not wired
            </button>
          </div>
        </div>

        {/* ── Draft schematic + checks ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Draft schematic (annotation 4) — the served composition, never a drawn
              graph we do not have. The schematic itself is the P8 screen. */}
          <div className="card">
            <h4>
              Draft schematic
              <span className="sub">composition as served</span>
            </h4>
            {nodes.length === 0 ? (
              <EmptyState variant="no_data" title="No topology reported" />
            ) : (
              <div
                style={{
                  border: "1px solid var(--sk-color-border-1)",
                  borderRadius: "var(--sk-radius-card)",
                  minHeight: 180,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  fontSize: 12,
                }}
              >
                <div style={{ color: "var(--sk-color-ink-2)" }}>
                  {[...classCounts.entries()].map(([cls, n]) => `${cls} · ${n}`).join("  |  ")}
                </div>
                <div style={{ color: "var(--sk-color-ink-2)" }}>
                  {meterGroups.length > 0 ? (
                    `${meterGroups.length} meter groups served`
                  ) : (
                    <>
                      Meter groups:{" "}
                      <StateWord state={st("NOT_MEASURED")} reason={R.noMeterMap} />
                    </>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "var(--sk-color-ink-2)" }}>
                    {noEdges ? "no connections served" : `${edges.length} connections served`}
                  </span>
                  <span
                    style={{
                      border: "1px dashed var(--sk-color-border-2)",
                      borderRadius: "var(--sk-radius-chip)",
                      padding: "3px 8px",
                      color: "var(--sk-color-ink-3)",
                      fontSize: 11,
                    }}
                  >
                    unassigned:{" "}
                    {noEdges ? (
                      <StateWord state={st("NOT_MEASURED")} reason={R.noTopology} />
                    ) : (
                      unassigned.length
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Checks before publish (annotation 5) */}
          <div className="card">
            <h4>Checks before publish</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {checks.map((c) => (
                <div
                  key={c.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: 10,
                    alignItems: "baseline",
                    fontSize: 12,
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                    <CheckMark mark={c.mark} />
                    {c.label}
                  </span>
                  <span style={{ color: "var(--sk-color-ink-3)" }}>{c.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title="Onboard site"
        family="admin"
        scope={scopeLine}
        showTimeControl={false}
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {versionChip}
            {/* No onboarding write path exists in Build 01: both controls stay in
                place and state that, instead of pretending to work. */}
            <button
              type="button"
              className="btn"
              style={notWiredStyle}
              disabled
              aria-disabled="true"
              title="As-built import is not wired yet: the platform serves no topology import endpoint."
            >
              Import as-built · not wired
            </button>
            <button
              type="button"
              className="btn"
              style={notWiredStyle}
              disabled
              aria-disabled="true"
              title="Publishing a topology is not wired yet: the platform serves no topology write endpoint."
            >
              Publish topology · not wired
            </button>
          </div>
        }
      />

      {/* Wizard step bar */}
      <div className="wizard">
        {STEPS.map((s, i) => (
          <div key={s} className={i === step ? "on" : i < step ? "done" : ""}>
            {i + 1} {s}
          </div>
        ))}
      </div>

      {/* The site this wizard is reading. Never defaulted (CLAUDE.md rule 6). */}
      <div className="card">
        <h4>
          Site
          <span className="sub">the wizard reads this site&apos;s asset graph and registry</span>
        </h4>
        {plantsQuery.isLoading ? (
          <Skeleton variant="text" width="14rem" />
        ) : plantsQuery.isError || plantsQuery.data?.status !== 200 ? (
          // A failed read is not an empty fleet: it is never shown as "no sites".
          <ErrorState
            title="Site list unavailable"
            message="The fleet list could not be read, so the site to onboard cannot be chosen."
            onRetry={() => void plantsQuery.refetch()}
          />
        ) : siteOptions.length === 0 ? (
          <EmptyState
            variant="no_data"
            title="No sites served"
            message="The fleet list returned no site identifier for this account, so no site can be onboarded here."
          />
        ) : (
          <Select
            label="Site identifier"
            items={siteOptions}
            selectedKey={site}
            onSelectionChange={setSite}
            placeholder="Choose a site"
            description="Site identifiers only — the registry's own key for the site."
          />
        )}
      </div>

      {body}
    </div>
  );
}

/* ── Derivations off the served contract ──────────────────────────────────── */

/** A figure state, typed from the generated enum without widening to string. */
function st(state: "NOT_MEASURED" | "CANNOT_CHECK" | "NOT_APPLICABLE"): FigureState {
  return state;
}

/**
 * The edge's type. The contract calls the field `type`; the platform currently
 * serves it as `edge_type`, so both are read and neither is invented.
 */
function edgeType(e: GraphEdge): string | null {
  if (typeof e.type === "string" && e.type) return e.type;
  const served = (e as unknown as Record<string, unknown>)["edge_type"];
  return typeof served === "string" && served ? served : null;
}

/**
 * The edge's rating. Shown only when the unit travels with it — a bare number
 * would imply a unit we were not given (UX-003 §6.2).
 */
function edgeRating(e: GraphEdge): string | null {
  const unit = (e as unknown as Record<string, unknown>)["rating_unit"];
  if (typeof e.rating !== "number" || typeof unit !== "string" || !unit) return null;
  return `${e.rating} ${unit}`;
}

/** Meters sitting in a meter group — from the registry, or withheld. */
function meterGroupCheck(
  assetsOk: boolean,
  withheld: Record<string, Reason | undefined>,
  meters: number,
  metersNoGroup: number,
): Check {
  const label = "Every meter has a group";
  if (!assetsOk) {
    return {
      mark: "withheld",
      label,
      detail: <StateWord state={st("CANNOT_CHECK")} reason={R.registryUnreadable} />,
    };
  }
  const served = withheld["meter_group_id"];
  if (served) {
    // The registry itself says no meter-to-inverter mapping is authored.
    return { mark: "withheld", label, detail: <StateWord state={st("NOT_MEASURED")} reason={served} /> };
  }
  if (meters === 0) {
    return { mark: "withheld", label, detail: "No device on this site is registered as a meter." };
  }
  return {
    mark: metersNoGroup === 0 ? "ok" : "warning",
    label,
    detail: `${metersNoGroup} of ${meters} registered meters sit in no group`,
  };
}

/** Devices fitted but not instrumented — the registry's own third state. */
function instrumentCheck(assetsOk: boolean, known: number, notInstrumented: number): Check {
  const label = "Data source mapped for every device";
  if (!assetsOk) {
    return {
      mark: "withheld",
      label,
      detail: <StateWord state={st("CANNOT_CHECK")} reason={R.registryUnreadable} />,
    };
  }
  if (known === 0) {
    return {
      mark: "withheld",
      label,
      detail: <StateWord state={st("CANNOT_CHECK")} reason={R.noSignalInventory} />,
    };
  }
  return {
    mark: notInstrumented === 0 ? "ok" : "warning",
    label,
    detail: `${notInstrumented} of ${known} devices are fitted but not instrumented · each stays partially monitored until a source is mapped`,
  };
}

/* ── Presentation ─────────────────────────────────────────────────────────── */

const notWiredStyle: React.CSSProperties = {
  borderColor: "var(--sk-color-border-2)",
  background: "var(--sk-color-surface-1)",
  color: "var(--sk-color-ink-3)",
  cursor: "not-allowed",
  whiteSpace: "nowrap",
};

function CheckMark({ mark }: { mark: Mark }): ReactNode {
  if (mark === "withheld") {
    // Nothing is being asserted, so no health colour is spent on it.
    return (
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          border: "1px solid var(--sk-color-ink-3)",
          borderRadius: 1,
          display: "inline-block",
        }}
      />
    );
  }
  if (mark === "fault") {
    return <span aria-hidden="true" style={{ width: 8, height: 8, background: "var(--sk-color-status-fault)", borderRadius: 1, display: "inline-block" }} />;
  }
  if (mark === "warning") {
    return <span aria-hidden="true" style={{ color: "var(--sk-color-status-warning)", fontSize: 10, lineHeight: 1 }}>▲</span>;
  }
  return <span aria-hidden="true" style={{ width: 8, height: 8, background: "var(--sk-color-status-ok)", borderRadius: "50%", display: "inline-block" }} />;
}
